import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dropdown } from 'primereact/dropdown'
import Button from '../../../components/ui/Button'
import CardPaginator from '../../../components/ui/CardPaginator'
import { confirmDialog } from '../../../components/ui/ConfirmDialog'
import DataTable from '../../../components/ui/DataTable'
import EmptyState from '../../../components/ui/EmptyState'
import FilterBar from '../../../components/ui/FilterBar'
import { useToast } from '../../../components/ui/ToastProvider'
import { isOptimisticLock } from '../../../api/apiError'
import { formatDate } from '../../../lib/datetime'
import { CARD_VIEW_BREAKPOINT, useMediaQuery } from '../../../lib/useMediaQuery'
import { formatDuration, shortTime } from '../duration'
import { useMeetingRecords, useTimePortfolioLookups } from '../hooks/useTimePortfolio'
import { meetingRecordColumns } from './MeetingRecordColumns'
import MeetingRecordFormDialog from './MeetingRecordFormDialog'
import type { MeetingRecord, MeetingRecordUpsert } from '../types'

/** Sayfa boyutu (anayasa: tüm listelerde 10). */
const PAGE_SIZE = 10

/** Arama gecikmesi — her tuşta sunucuya gidilmez. */
const SEARCH_DEBOUNCE_MS = 300

/** "Otorite Zorunlu" süzgecinin üç durumu; `null` = filtre yok. */
type AuthorityFilter = boolean | null

/**
 * **Zaman Portföyü → Toplantı Kaydı** sekmesi: süzgeçli liste + ekle/düzenle/sil.
 *
 * <p><b>Sayfalama ve süzme sunucudadır</b> (kardeş parametre ekranlarının aksine): bu bir hareket
 * tablosudur, her gün büyür ve kullanıcı en çok "şu tarih aralığında ne oldu?" diye bakar. Bu yüzden
 * tablo `lazy` çalışır ve kolon başlıklarında sıralama yoktur — sunucu sırayı sabitliyor (en yeni
 * üstte), başlıklar yalnız açık sayfayı sıralayıp kullanıcıya yanlış söylerdi.
 *
 * <p>Dar ekranda tablo yerine <b>kart listesi</b> çizilir (anayasa: dar ekran kart kalıbı); sayfalama
 * orada paylaşılan `ui/CardPaginator` ile sürer.
 */
export default function MeetingRecordsTab() {
  const { t } = useTranslation()
  const toast = useToast()
  const isMobile = useMediaQuery(CARD_VIEW_BREAKPOINT)

  const [first, setFirst] = useState(0)
  const [rows, setRows] = useState(PAGE_SIZE)
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [kind, setKind] = useState<number | null>(null)
  const [category, setCategory] = useState<number | null>(null)
  const [authority, setAuthority] = useState<AuthorityFilter>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<MeetingRecord | null>(null)

  useEffect(() => {
    const id = setTimeout(() => {
      setDebounced(search.trim())
      setFirst(0)
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(id)
  }, [search])

  // Tarih aralığı süzgeci ekrandan KALDIRILDI (kullanıcı isteği 23.09.2026). Sunucu tarafındaki
  // `from`/`to` parametreleri duruyor ve opsiyonel — geri istenirse tek paylaşılan bileşen eklenir.
  const query = useMemo(
    () => ({
      meetingOrActivityId: kind,
      meetingTypeId: category,
      authorityMandatory: authority,
      q: debounced || null,
      page: Math.floor(first / rows),
      size: rows,
    }),
    [kind, category, authority, debounced, first, rows],
  )

  const { list, create, update, remove } = useMeetingRecords(query)
  const lookups = useTimePortfolioLookups()

  const items = list.data?.content ?? []
  const total = list.data?.page.totalElements ?? 0
  const submitting = create.isPending || update.isPending
  const busyId = remove.isPending ? (remove.variables ?? null) : null

  const durationLabels = {
    hour: t('timePortfolio.duration.hour'),
    minute: t('timePortfolio.duration.minute'),
  }

  const notify = (severity: 'success' | 'error', summary: string) =>
    toast.show({ severity, summary, life: 3000 })

  const resetToFirstPage = () => setFirst(0)

  const openCreate = () => {
    setEditing(null)
    setFormOpen(true)
  }
  const openEdit = (row: MeetingRecord) => {
    setEditing(row)
    setFormOpen(true)
  }

  // Bayat damga (409) → form kapanır ve liste tazelenir; diğer hatalar merkezî toast'ta.
  const handleMutationError = (err: unknown) => {
    if (isOptimisticLock(err)) {
      setFormOpen(false)
      list.refetch()
    }
  }

  const onSubmit = (payload: MeetingRecordUpsert) => {
    if (editing) {
      update.mutate(
        { id: editing.id, body: { ...payload, modDate: editing.modDate } },
        {
          onSuccess: () => {
            notify('success', t('timePortfolio.toast.updated'))
            setFormOpen(false)
          },
          onError: handleMutationError,
        },
      )
      return
    }
    create.mutate(payload, {
      onSuccess: () => {
        notify('success', t('timePortfolio.toast.created'))
        setFormOpen(false)
        resetToFirstPage()
      },
      onError: handleMutationError,
    })
  }

  const onDelete = (row: MeetingRecord) => {
    confirmDialog({
      header: t('timePortfolio.delete.title'),
      message: t('timePortfolio.delete.confirm', { name: row.name }),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('timePortfolio.delete.accept'),
      accept: () =>
        remove.mutate(row.id, {
          onSuccess: () => notify('success', t('timePortfolio.toast.deleted')),
        }),
    })
  }

  const kindOptions = [
    { label: t('timePortfolio.filters.all'), value: null },
    ...(lookups.data?.kinds ?? []).map((k) => ({ label: k.name, value: k.id })),
  ]
  const categoryOptions = [
    { label: t('timePortfolio.filters.all'), value: null },
    ...(lookups.data?.categories ?? []).map((c) => ({ label: c.name, value: c.id })),
  ]
  const authorityOptions = [
    { label: t('timePortfolio.filters.all'), value: null },
    { label: t('timePortfolio.authority.yes'), value: true },
    { label: t('timePortfolio.authority.no'), value: false },
  ]

  const empty = (
    <EmptyState
      title={t('timePortfolio.meeting.empty.title')}
      description={t('timePortfolio.meeting.empty.desc')}
    />
  )

  return (
    <div className="tp-tab">
      <MeetingRecordFormDialog
        visible={formOpen}
        initial={editing}
        lookups={lookups.data}
        submitting={submitting}
        onSubmit={onSubmit}
        onHide={() => setFormOpen(false)}
      />

      <div className="tp-tab-actions">
        <Button
          icon="pi pi-plus"
          label={t('timePortfolio.meeting.actions.new')}
          onClick={openCreate}
        />
      </div>

      <FilterBar
        searchValue={search}
        searchPlaceholder={t('timePortfolio.meeting.search')}
        onSearch={setSearch}
        onRefresh={() => list.refetch()}
      >
        <Dropdown
          value={kind}
          options={kindOptions}
          onChange={(e) => {
            setKind(e.value)
            resetToFirstPage()
          }}
          placeholder={t('timePortfolio.meeting.columns.kind')}
          aria-label={t('timePortfolio.meeting.columns.kind')}
        />
        <Dropdown
          className="tp-filter-meeting-type"
          value={category}
          options={categoryOptions}
          onChange={(e) => {
            setCategory(e.value)
            resetToFirstPage()
          }}
          placeholder={t('timePortfolio.meeting.columns.category')}
          aria-label={t('timePortfolio.meeting.columns.category')}
        />
        <Dropdown
          value={authority}
          options={authorityOptions}
          onChange={(e) => {
            setAuthority(e.value)
            resetToFirstPage()
          }}
          placeholder={t('timePortfolio.meeting.columns.authority')}
          aria-label={t('timePortfolio.meeting.columns.authority')}
        />
      </FilterBar>

      {isMobile ? (
        <div className="ui-card-list">
          {list.isLoading &&
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="ui-card ui-card--skeleton" aria-hidden />
            ))}
          {!list.isLoading && items.length === 0 && empty}
          {!list.isLoading &&
            items.map((row) => (
              <div key={row.id} className="ui-card">
                <div className="ui-card-top">
                  <span className="ui-card-sub">
                    {formatDate(row.recordDate)} · {shortTime(row.startTime)}-
                    {shortTime(row.finishTime)}
                  </span>
                  <div className="ui-card-actions ui-card-actions--compact">
                    <Button
                      icon="pi pi-pencil"
                      rounded
                      text
                      aria-label={t('timePortfolio.actions.editNamed', { name: row.name })}
                      onClick={() => openEdit(row)}
                    />
                    <Button
                      icon="pi pi-trash"
                      rounded
                      text
                      severity="danger"
                      aria-label={t('timePortfolio.actions.deleteNamed', { name: row.name })}
                      disabled={busyId === row.id}
                      onClick={() => onDelete(row)}
                    />
                  </div>
                </div>
                <div className="ui-card-head">
                  <span className="ui-card-title">{row.name}</span>
                </div>
                <div className="ui-card-top">
                  <span className="ui-card-sub">
                    {row.meetingOrActivityName} · {row.meetingTypeName ?? ''}
                  </span>
                  <span className="ui-card-sub">
                    {formatDuration(row.durationMinutes, durationLabels)}
                  </span>
                </div>
              </div>
            ))}
          {!list.isLoading && total > rows && (
            <CardPaginator first={first} rows={rows} totalRecords={total} onPage={setFirst} />
          )}
        </div>
      ) : (
        <DataTable
          value={items}
          loading={list.isLoading}
          lazy
          first={first}
          rows={rows}
          totalRecords={total}
          onPage={(e) => {
            setFirst(e.first)
            setRows(e.rows)
          }}
          emptyMessage={empty}
        >
          {meetingRecordColumns({ t, busyId, onEdit: openEdit, onDelete })}
        </DataTable>
      )}
    </div>
  )
}
